import type { GridColumn, Project } from '../types';
import { getExecutionProgress } from './stages';

// RAG health for projects currently in Execution, derived from planning
// milestones (planning.milestones JSON): red = a non-completed milestone is
// past its end date; orange = one ends within SOON_DAYS; green otherwise.
// The stored key is `estimated_end` (snake case, see formToPayload); reading
// only `estimatedEnd` (the form's name) left every project green.
// Returns null for projects not in the Execution stage — callers render nothing.
export type ExecutionHealthLevel = 'green' | 'orange' | 'red';
export interface ExecutionHealth {
  level: ExecutionHealthLevel;
  milestone?: string; // name of the milestone that triggered red/orange
}

const SOON_DAYS = 14;

export function getExecutionHealth(project: Project, pipelineColumns: GridColumn[]): ExecutionHealth | null {
  const label = pipelineColumns.find(c => c.id === project.status_id)?.label ?? '';
  if (getExecutionProgress(label) !== 'execution') return null;
  return milestoneHealth(project.planning);
}

/**
 * The project's status from its milestones alone (no stage check): `planning`
 * is the planning JSON (object or string) or `{ milestones }` from the form —
 * milestones with `estimated_end` (stored) or `estimatedEnd` (form).
 */
export function milestoneHealth(planning: unknown): ExecutionHealth {
  // planning is a JSONB column: pg may hand it over already parsed (object)
  // or as a JSON string depending on the code path — accept both.
  let milestones: Array<{ status?: string; estimated_end?: string | null; estimatedEnd?: string; name?: string }> = [];
  try {
    const parsed = typeof planning === 'object' && planning !== null ? planning : JSON.parse((planning as string) || '{}');
    milestones = (parsed as { milestones?: typeof milestones }).milestones ?? [];
  } catch {
    // malformed planning JSON — treat as no milestones
  }

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const soonCutoff = todayStart + SOON_DAYS * 86_400_000;

  let dueSoon: string | undefined;
  for (const m of milestones) {
    const end = m?.estimated_end || m?.estimatedEnd;
    if (!m || m.status === 'completed' || m.status === 'cancelled' || !end) continue;
    const due = Date.parse(end);
    if (Number.isNaN(due)) continue;
    if (due < todayStart) return { level: 'red', milestone: m.name || undefined };
    if (due <= soonCutoff && dueSoon === undefined) dueSoon = m.name || '';
  }
  if (dueSoon !== undefined) return { level: 'orange', milestone: dueSoon || undefined };
  return { level: 'green' };
}

export const HEALTH_BADGE_CLASS: Record<ExecutionHealthLevel, string> = { green: 'badge-ok', orange: 'badge-warn', red: 'badge-err' };

// Static key map so the i18n checker can resolve the keys.
export const HEALTH_TITLE_KEY: Record<ExecutionHealthLevel, string> = {
  green: 'card.health.green',
  orange: 'card.health.orange',
  red: 'card.health.red',
};
